import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { SlotWorkflowClient } from '@frontend/slot/api';
import { Spot } from '@frontend/spot/types';
import { ExtendedPudoTransaction } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import { BulkMoveLocationState } from './bulk-move-location-state';

export interface BulkMoveSelectSpotViewProps {
    transactions: ExtendedPudoTransaction[];
    spots: Spot[];
    selectedSpotId: string | null;
    setSelectedSpotId: (spotId: string) => void;
    isLoading: boolean;
    error: ReactNode | null;
    canContinue: boolean;
    handleContinue: () => void;
    handleBack: () => void;
}

export const useBulkMoveSelectSpot = (): BulkMoveSelectSpotViewProps => {
    const navigate = useKioskNavigate();
    const location = useLocation();
    const navigationState = useAppSelector((state) => state.navigation);
    const { getFunctionality } = useLicenseRepository();

    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const currentSpotId = navigationState.spot?.id ?? null;
    const bulkFunctionality = getFunctionality(Application.PUDO, Feature.BULK_MOVE_TRANSACTION);

    const transactions = (location.state as BulkMoveLocationState | null)?.transactions ?? [];

    const [spots, setSpots] = useState<Spot[]>([]);
    const [selectedSpotId, setSelectedSpotId] = useState<string | null>(currentSpotId);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);

    useEffect(() => {
        if (!accountId || transactions.length === 0) {
            setIsLoading(false);
            setError(transactions.length === 0 ? CommonMessage.ERRORS.DEFAULT : CommonMessage.ERRORS.FAILED_TO_LOAD);
            return;
        }

        setIsLoading(true);
        setError(null);
        SlotWorkflowClient.postAvailableSpots(
            accountId,
            {
                account_id: accountId,
                application: Application.PUDO,
                feature: Feature.BULK_MOVE_TRANSACTION,
                functionality_id: bulkFunctionality?.id
            },
            { sort_field: 'name', sort_direction: 'asc', size: '200', index: '0' }
        )
            .then((response) => {
                const results = response.results ?? [];
                setSpots(results);
                if (currentSpotId && results.some((spot) => spot.id === currentSpotId)) {
                    setSelectedSpotId(currentSpotId);
                } else if (results.length > 0) {
                    setSelectedSpotId(results[0].id);
                } else {
                    setSelectedSpotId(null);
                }
            })
            .catch((err) => {
                Logger.error('Failed to load bulk move destination SPOTs', err);
                setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => setIsLoading(false));
    }, [accountId, bulkFunctionality?.id, currentSpotId, transactions.length]);

    const handleContinue = useCallback(() => {
        if (!selectedSpotId || transactions.length === 0) return;
        navigate(`/spots/layout/pudo/bulk-move-transactions/move/spot/${selectedSpotId}`, {
            state: { transactions } satisfies BulkMoveLocationState
        });
    }, [navigate, selectedSpotId, transactions]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        transactions,
        spots,
        selectedSpotId,
        setSelectedSpotId,
        isLoading,
        error,
        canContinue: !!selectedSpotId && transactions.length > 0 && !error && !isLoading,
        handleContinue,
        handleBack
    };
};
